import { SearchParamsV2 } from 'lib/AutolistAPI';
import { AdInfo } from 'lib/CarGurusAds';

export interface JumpstartInfoInterface {
  data: {
    attributes: {
      jumpstart_info: {
        mak: string;
        mod: string;
        type: string;
        fuel: string;
        style: string;
        yr: string;
        jam_unit: string;
        jam_unit_attributes: {
          jam_prefix: string;
          source: string;
          focus: string;
          condition: string;
        };
        pub: string;
      };
    };
  };
}

export interface LinkSet {
  title?: string;
  search_urls?: {
    link_text: string;
    url: string;
  }[];
}

export interface SearchUrlAttributes {
  has_analysis: boolean;
  jumpstart_info?: JumpstartInfoInterface;
  ad_info?: AdInfo;
  search_params: SearchParamsV2;
  title?: string;
  title_es?: string;
  meta_description?: string;
  meta_description_es?: string;
  url?: string;
  h1_tag?: string;
  h1_tag_es?: string;
  link_set: LinkSet[];
  breadcrumb_structured: {
    '@context': string;
    '@type': string;
    ItemListElement: {
      '@type': string;
      position: number;
      name: string;
      item: string;
    }[];
  };
  type?: string;
  make_name?: string;
  model_name?: string;
  location_display_name?: string;
  year?: string;
  location_id?: number;
  trim?: string;
}

export interface SearchURL {
  data: {
    attributes: SearchUrlAttributes;
  };
}

export enum SearchUrlTypes {
  MakeSearchUrl = 'MakeSearchUrl',
  MakeModelSearchUrl = 'MakeModelSearchUrl',
  MakeModelLocationSearchUrl = 'MakeModelLocationSearchUrl',
  MakeModelYearSearchUrl = 'MakeModelYearSearchUrl',
  LocationSearchUrl = 'LocationSearchUrl',
}

export interface DealerReviewsInterface {
  pagination: {
    currentPage: number;
    totalPages: number;
  };
  dealerReviewSummary: DealerReviewSummary;
  dealerReviews: DealerReviewInterface[];
}

export interface DealerReviewSummary {
  reviewCount: number;
  overallRating: number;
  recommendationCount: number;
  countsByRating: {
    fiveStar: number;
    fourStar: number;
    threeStar: number;
    twoStar: number;
    oneStar: number;
  };
  averagesByCategory: {
    pricingRating: number;
    serviceRating: number;
    helpfulnessRating: number;
    responsivenessRating: number;
  };
}

export interface DealerReviewInterface {
  id: number;
  writtenAt: string;
  makeName: string;
  modelId: string;
  modelName: string;
  year: number;
  modelYear: number;
  dealerName: string;
  bestLocationDisplayName: string;
  reviewTitle: string;
  review: string;
  extraReview: string;
  reviewerName: string;
  overallRating: number;
  facilityRating: number;
  processRating: number;
  qualityRating: number;
  serviceRating: number;
  priceRating: number;
  helpRating: number;
}

export interface DealerReviewsApiResponse {
  pagination: {
    current_page: number;
    total_pages: number;
  };
  dealer_review_summary: {
    review_count: number;
    overall_rating: number;
    recommendation_count: number;
    counts_by_rating: {
      '5_star': number;
      '4_star': number;
      '3_star': number;
      '2_star': number;
      '1_star': number;
    };
    averages_by_category: {
      pricing_rating: number;
      service_rating: number;
      helpfulness_rating: number;
      responsiveness_rating: number;
    };
  };
  dealer_reviews: DealerReviewApiResponse[];
}

export interface DealerReviewApiResponse {
  id: number;
  written_at: string;
  model_id: string;
  make_name: string;
  model_name: any;
  year: number;
  model_year: number;
  dealer_name: string;
  best_location_display_name: string;
  review_title: string;
  review: string;
  extra_review: string;
  reviewer_name: string;
  overall_rating: number;
  facility_rating: number;
  process_rating: number;
  quality_rating: number;
  service_rating: number;
  price_rating: number;
  help_rating: number;
}

export interface ModelReviewsApiResponse {
  pagination: {
    current_page: number;
    total_pages: number;
  };
  model_review_summary: {
    review_count: number;
    overall_rating: number;
    counts_by_rating: {
      '5_star': number;
      '4_star': number;
      '3_star': number;
      '2_star': number;
      '1_star': number;
    };
    averages_by_category: {
      value_rating: number;
      performance_rating: number;
      style_rating: number;
      comfort_rating: number;
      fuel_economy_rating: number;
      maintenance_rating: number;
    };
  };
  model_reviews: ModelReviewApiResponse[];
}

export interface ModelReviewApiResponse {
  id: number;
  make_name: string;
  model_name: string;
  model_year: number;
  title: string;
  body: string;
  pros: string;
  cons: string;
  story: string;
  best_location_display_name: string;
  reviewer_name: string;
  overall_rating: number;
  value_rating: number;
  performance_rating: number;
  style_rating: number;
  comfort_rating: number;
  fuel_economy_rating: number;
  maintenance_rating: number;
}

export interface ModelReviewInterface {
  id: number;
  makeName: string;
  modelName: string;
  modelYear: number;
  title: string;
  body: string;
  pros: string;
  cons: string;
  story: string;
  bestLocationDisplayName: string;
  reviewerName: string;
  overallRating: number;
  valueRating: number;
  performanceRating: number;
  styleRating: number;
  comfortRating: number;
  fuelEconomyRating: number;
  maintenanceRating: number;
}

export interface DrivingExperienceInterface {
  id: number;
  author: string;
  city: string;
  driving_tip: string;
  buying_tip: string;
  favorite_memory: string;
  favorite_road: string;
}

export interface ModelReviewsInterface {
  pagination: {
    currentPage: number;
    totalPages: number;
  };
  ownerReviewSummary: ModelReviewSummary;
  ownerReviews: ModelReviewInterface[];
}

export interface ModelReviewSummary {
  reviewCount: number;
  overallRating: number;
  breakdownByRatingPercentages: {
    fiveStar: number;
    fourStar: number;
    threeStar: number;
    twoStar: number;
    oneStar: number;
  };
  breakdownByCategoryAverages: {
    valueRating: number;
    styleRating: number;
    performanceRating: number;
    comfortRating: number;
    fuelEconomyRating: number;
    maintenanceRating: number;
  };
}
